import React, { useState } from 'react'
import { Modal, Button, Tree, Toast, Tooltip } from '@ow/comps'
import { Alert, Textarea } from '@mantine/core'
import { IconMaps } from '@ow/icons'
import { useTbViewStore } from '@vt/hooks'
import { pick } from 'lodash-es'
import { useProjectValidator } from './hooks/tab'
import { matrixDataCheck } from './utils'

// 指标填充示例
const standardDemo =
  '公共环境&无争吵谩骂、使用低俗语言现象\n公共环境&无躺卧公共座椅现象\n公共环境&公共卫生间保洁及时，无明显异味&公共卫生间干净整洁\n公共环境&公共卫生间保洁及时，无明显异味&公共卫生间无异味'
const standardTip =
  '同级如需添加多个指标，请换行输入，例如\n公共环境&无争吵谩骂、使用低俗语言现象\n公共环境&无躺卧公共座椅现象\n公共环境&公共卫生间保洁及时，无明显异味&公共卫生间干净整洁\n公共环境&公共卫生间保洁及时，无明显异味&公共卫生间无异味'
const areaDemo =
  '北京市&海淀区\n北京市&东城区\n北京市&西城区&首都博物馆\n北京市&西城区&北京动物园'
const areaTip =
  '同级如需添加多个点位，请换行输入，例如\n北京市&海淀区\n北京市&东城区\n北京市&西城区&首都博物馆\n北京市&西城区&北京动物园'

const Batch = function ({
  type,
  refreshTreeData,
  isAddChild = false,
  parentId,
  children,
}) {
  const [inputData, setInputData] = useState()
  const [treeData, setTreeData] = useState([])
  const [error, setError] = useState()
  const [loading, setLoading] = useState(false)

  const { ditto, clickedInfo } = useTbViewStore((state) =>
    pick(state, ['ditto', 'clickedInfo'])
  )

  const projectValidator = useProjectValidator()

  const { clickedId = null } = clickedInfo

  const buildNode = (nodeList) => {
    const treeMap = new Map()
    nodeList.forEach((node) => {
      const head = node[0]
      const tail = node.slice(1, node.length)
      if (!head) {
        return
      }
      if (treeMap.has(head)) {
        treeMap.get(head).push(tail)
      } else {
        treeMap.set(head, tail.length > 0 ? [tail] : [])
      }
    })
    treeMap.forEach((value, key) => {
      treeMap.set(key, buildNode(value))
    })
    return treeMap
  }

  let index = 0
  const transformTree = (node) => {
    const arr_ = []
    node.forEach((value, key) => {
      index += 1
      arr_.push({
        title: key,
        value: index,
        children: transformTree(value),
      })
    })
    return arr_
  }

  const previewToTree = (value) => {
    const arr = value
      .split('\n')
      .filter((v) => v !== '')
      .map((line) => {
        return line
          .split('&')
          .map((l) => {
            return l.replace(/\s/g, '')
          })
          .filter((m) => m !== '')
      })
    setTreeData(transformTree(buildNode(arr)))
  }

  const submitData = (close) => {
    const currentNodes = {}
    if (projectValidator('createRepeatlyCheck')) {
      ditto.data.forEach((node) => {
        currentNodes[node.title] = node
      })
    }

    const checkError = matrixDataCheck(
      inputData.split('\n').map((row) => row.split('&')),
      currentNodes
    )

    if (checkError) {
      setError(checkError)
      return
    }

    const list = inputData.split('\n')
    let parent = null
    if (parentId) {
      parent = parentId
    } else if (isAddChild) {
      parent = clickedId
    }

    setLoading(true)
    ditto
      .batchCreateTbData(parent, list)
      .then((res) => {
        close()
        refreshTreeData(res)
        setInputData()
        Toast.success({ title: '添加成功' })
      })
      .catch((err) => {
        Toast.error({ title: err.message })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Modal
      trigger={children}
      closeOnDocumentClick={!loading}
      closeOnEscape={!loading}
      showCloseButton={!loading}
      width="1400px"
      title="批量添加"
      // eslint-disable-next-line react/no-unstable-nested-components
      footer={({ close }) => (
        <>
          <Button
            type="primary"
            opacityType="sm"
            style={{ marginRight: '10px' }}
            loading={loading}
            onClick={() => {
              submitData(close)
            }}
            disabled={!inputData || inputData.trim() === ''}
          >
            确定
          </Button>
          <Button
            type="secondary"
            opacityType="sm"
            style={{ marginRight: '10px' }}
            disabled={loading}
            onClick={() => {
              setInputData()
              close()
            }}
          >
            取消
          </Button>
        </>
      )}
    >
      <div className="flex">
        <div className="w-1/2 border-r pr-4">
          <div className="flex flex-col gap-4">
            <Alert
              icon={
                <IconMaps
                  type="warning"
                  fill="var(--ow-color-primary-700)"
                  width={18}
                  height={18}
                />
              }
              style={{ backgroundColor: 'var(--ow-color-primary-100)' }}
            >
              支持多级添加{type === 'standard' ? '指标' : '点位'}
              ，各级间使用“&”进行分隔
            </Alert>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="font-bold">文本编辑</div>
                <Tooltip
                  trigger={
                    <div
                      className="text-primary-700 text-sm cursor-pointer"
                      onClick={() => {
                        setInputData(
                          type === 'standard' ? standardDemo : areaDemo
                        )
                        previewToTree(
                          type === 'standard' ? standardDemo : areaDemo
                        )
                        setError()
                      }}
                    >
                      填充示例
                    </div>
                  }
                  position="top"
                  on={['hover', 'focus']}
                >
                  将会覆盖已有内容
                </Tooltip>
              </div>
              <div>
                <Textarea
                  minRows={14}
                  onChange={(e) => {
                    setInputData(e.currentTarget.value)
                    previewToTree(e.currentTarget.value)
                    setError()
                  }}
                  value={inputData}
                  error={error}
                  placeholder={type === 'standard' ? standardTip : areaTip}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="w-1/2 pl-4 flex flex-col gap-4">
          <div className="font-bold">效果预览</div>
          <Tree
            data={treeData}
            titleLength={10}
            height="100%"
            label="title"
            showLine={false}
          />
        </div>
      </div>
    </Modal>
  )
}

export default Batch
